import React, { useState } from 'react';
import api from '../../services/api';
import { WorkoutFormData } from '../../types';
import { useUnits } from '../../contexts/UnitsContext';
import './LogWorkoutDrawer.css';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const LogWorkoutDrawer: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restDayWarning, setRestDayWarning] = useState<string | null>(null);
  const { isMetric, distLabel, elevLabel, inputDistToMiles, inputElevToFeet, milesToDisplay, feetToDisplay } = useUnits();

  const [formData, setFormData] = useState<WorkoutFormData>({
    workout_date: new Date().toISOString().split('T')[0],
    distance_miles: 0,
    duration_seconds: 0,
    avg_heart_rate: undefined,
    max_heart_rate: undefined,
    elevation_gain_ft: undefined,
    perceived_effort: undefined,
    workout_type: undefined,
    notes: undefined,
  });

  const [duration, setDuration] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [calculatedPace, setCalculatedPace] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value === '' ? undefined : value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === '') { setFormData(prev => ({ ...prev, [name]: 0 })); return; }
    const num = parseFloat(value);
    if (!isNaN(num)) {
      if (name === 'distance_miles') {
        const miles = inputDistToMiles(num);
        setFormData(prev => ({ ...prev, distance_miles: miles }));
        if (miles > 0) calculatePace(miles, formData.duration_seconds);
      } else if (name === 'elevation_gain_ft') {
        setFormData(prev => ({ ...prev, elevation_gain_ft: inputElevToFeet(num) }));
      } else {
        setFormData(prev => ({ ...prev, [name]: num }));
      }
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newDur = { ...duration, [name]: parseInt(value) || 0 };
    setDuration(newDur);
    const total = newDur.hours * 3600 + newDur.minutes * 60 + newDur.seconds;
    setFormData(prev => ({ ...prev, duration_seconds: total }));
    if (formData.distance_miles > 0) calculatePace(formData.distance_miles, total);
  };

  const calculatePace = (distMiles: number, sec: number) => {
    if (distMiles > 0 && sec > 0) {
      const ps = sec / distMiles;
      const displayPs = isMetric ? ps * 0.621371 : ps;
      const unit = isMetric ? 'km' : 'mi';
      setCalculatedPace(`${Math.floor(displayPs / 60)}:${String(Math.floor(displayPs % 60)).padStart(2, '0')}/${unit}`);
    } else {
      setCalculatedPace('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.distance_miles <= 0) { setError('Distance must be greater than 0'); return; }
    if (formData.duration_seconds <= 0) { setError('Duration must be greater than 0'); return; }

    setSaving(true);
    setError(null);
    try {
      const response = await api.logWorkout(formData);
      if (response.rest_day_warning) setRestDayWarning(response.rest_day_warning);
      setTimeout(() => { onSuccess(); onClose(); }, response.rest_day_warning ? 2500 : 0);
    } catch (err: any) {
      setError(err.message || 'Failed to log workout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="drawer-backdrop" onClick={onClose} />

      {/* Drawer panel */}
      <div className="log-workout-drawer">
        <div className="drawer-header">
          <h2>Log Workout</h2>
          <button className="drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="drawer-body">
          {error && <div className="drawer-error">{error}</div>}
          {restDayWarning && <div className="drawer-warning">{restDayWarning}</div>}

          <form onSubmit={handleSubmit}>
            {/* Date & Distance */}
            <div className="drawer-field">
              <label>Date</label>
              <input type="date" name="workout_date" value={formData.workout_date} onChange={handleChange} required />
            </div>

            <div className="drawer-row">
              <div className="drawer-field">
                <label>Distance ({distLabel})</label>
                <input type="number" step="0.01" name="distance_miles" value={formData.distance_miles ? milesToDisplay(formData.distance_miles) : ''} onChange={handleNumberChange} placeholder={isMetric ? '8.0' : '5.0'} required />
              </div>
              <div className="drawer-field">
                <label>Elevation ({elevLabel})</label>
                <input type="number" name="elevation_gain_ft" value={formData.elevation_gain_ft ? feetToDisplay(formData.elevation_gain_ft) : ''} onChange={handleNumberChange} placeholder="Optional" />
              </div>
            </div>

            {/* Duration */}
            <div className="drawer-field">
              <label>Duration</label>
              <div className="duration-inputs">
                <input type="number" name="hours" min="0" value={duration.hours} onChange={handleDurationChange} placeholder="HH" />
                <span>:</span>
                <input type="number" name="minutes" min="0" max="59" value={duration.minutes} onChange={handleDurationChange} placeholder="MM" />
                <span>:</span>
                <input type="number" name="seconds" min="0" max="59" value={duration.seconds} onChange={handleDurationChange} placeholder="SS" />
              </div>
              {calculatedPace && <div className="calculated-pace">Pace: {calculatedPace}</div>}
            </div>

            {/* HR */}
            <div className="drawer-row">
              <div className="drawer-field">
                <label>Avg HR (bpm)</label>
                <input type="number" name="avg_heart_rate" value={formData.avg_heart_rate || ''} onChange={handleNumberChange} placeholder="Optional" />
              </div>
              <div className="drawer-field">
                <label>Max HR (bpm)</label>
                <input type="number" name="max_heart_rate" value={formData.max_heart_rate || ''} onChange={handleNumberChange} placeholder="Optional" />
              </div>
            </div>

            {/* Type & Effort */}
            <div className="drawer-row">
              <div className="drawer-field">
                <label>Type</label>
                <select name="workout_type" value={formData.workout_type || ''} onChange={handleChange}>
                  <option value="">Select…</option>
                  <option value="Easy">Easy</option>
                  <option value="Long Run">Long Run</option>
                  <option value="Tempo">Tempo</option>
                  <option value="Intervals">Intervals</option>
                  <option value="Hill Repeats">Hill Repeats</option>
                  <option value="Strength Training">Strength Training</option>
                </select>
              </div>
              <div className="drawer-field">
                <label>Effort</label>
                <select name="perceived_effort" value={formData.perceived_effort || ''} onChange={handleChange}>
                  <option value="">Select…</option>
                  <option value="Great">Great</option>
                  <option value="Good">Good</option>
                  <option value="OK">OK</option>
                  <option value="Hard">Hard</option>
                  <option value="Terrible">Terrible</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div className="drawer-field">
              <label>Notes</label>
              <textarea name="notes" value={formData.notes || ''} onChange={handleChange} rows={3} placeholder="How did it feel? Any observations…" />
            </div>

            <div className="drawer-actions">
              <button type="button" className="btn-drawer-cancel" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit" className="btn-drawer-submit" disabled={saving}>
                {saving ? 'Saving…' : 'Log Workout'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LogWorkoutDrawer;
